/**
 * Maison Atlas — Bloc « code de bienvenue »
 * Fichier : assets/ma-welcome-code.js
 * ------------------------------------------------------------
 * Gere uniquement le bouton « Copier le code ».
 *
 * Le lien « Appliquer le code » est un <a href> vers l'URL native
 * /discount/CODE?redirect=/cart : il n'a besoin d'aucun script et
 * continue de fonctionner si ce fichier ne se charge pas.
 *
 * Aucun libelle n'est ecrit ici. Les trois etats du bouton sont
 * lus sur les attributs data-label-* poses par le snippet, qui
 * les traduit via le filtre `t`.
 *
 * Copie : Clipboard API quand elle est disponible (contexte
 * securise), repli sur document.execCommand('copy') sinon —
 * Safari iOS en WebView et les contextes non-HTTPS.
 */

const MA_PROMO_FEEDBACK_MS = 2000;

class MaWelcomeCode extends HTMLElement {
  #timer = null;

  connectedCallback() {
    this.button = this.querySelector('[data-ma-promo-copy]');
    this.buttonLabel = this.querySelector('[data-ma-promo-copy-label]');
    this.status = this.querySelector('[data-ma-promo-status]');

    if (!this.button) return;

    this.labels = {
      copy: this.dataset.labelCopy || '',
      copied: this.dataset.labelCopied || '',
      failed: this.dataset.labelFailed || '',
    };

    this.onClick = this.onClick.bind(this);
    this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    if (this.#timer) clearTimeout(this.#timer);
  }

  get code() {
    return this.dataset.code || this.querySelector('[data-ma-promo-code]')?.textContent?.trim() || '';
  }

  async onClick() {
    const code = this.code;
    if (!code) return;

    const copied = await this.copy(code);
    this.feedback(copied);
  }

  async copy(text) {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        // On tombe volontairement sur le repli ci-dessous.
      }
    }
    return this.copyLegacy(text);
  }

  /**
   * Repli execCommand. Le champ doit etre dans le document et
   * visible du point de vue du moteur — d'ou l'opacite nulle
   * plutot que display:none, qui rendrait la selection inerte.
   */
  copyLegacy(text) {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.setAttribute('aria-hidden', 'true');
    field.style.position = 'fixed';
    field.style.top = '0';
    field.style.left = '0';
    field.style.width = '1px';
    field.style.height = '1px';
    field.style.padding = '0';
    field.style.border = 'none';
    field.style.opacity = '0';

    document.body.appendChild(field);

    let ok = false;
    try {
      field.select();
      field.setSelectionRange(0, text.length);
      ok = document.execCommand('copy');
    } catch (error) {
      ok = false;
    }

    document.body.removeChild(field);
    return ok;
  }

  feedback(success) {
    const label = success ? this.labels.copied : this.labels.failed;

    if (this.buttonLabel) this.buttonLabel.textContent = label;
    if (this.status) this.status.textContent = label;
    this.button.dataset.state = success ? 'done' : 'error';

    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      if (this.buttonLabel) this.buttonLabel.textContent = this.labels.copy;
      if (this.status) this.status.textContent = '';
      delete this.button.dataset.state;
      this.#timer = null;
    }, MA_PROMO_FEEDBACK_MS);
  }
}

if (!customElements.get('ma-welcome-code')) {
  customElements.define('ma-welcome-code', MaWelcomeCode);
}
